import { parse } from 'csv-parse/browser/esm/sync';

import { Role } from '@revelio/data-access';

import { type BulkUserPayload, MAX_BULK_USERS } from './admin-bulk-users.api';

const REQUIRED_HEADERS = ['name', 'email', 'password'] as const;

const cellToString = (cell: unknown): string => {
  if (cell === null || cell === undefined) {
    return '';
  }

  return String(cell);
};

export const parseBulkUserCsv = async (
  file: File,
  clientName: string
): Promise<BulkUserPayload[]> => {
  if (!file.name.toLowerCase().endsWith('.csv')) {
    throw new Error('Upload a CSV file.');
  }

  const rows = parse(await file.text(), {
    bom: true,
    skip_empty_lines: true,
  }) as unknown[][];

  const headerRow = rows[0];
  if (!headerRow) {
    throw new Error('The CSV file is empty.');
  }

  const headers = headerRow.map((header) =>
    cellToString(header).trim().toLowerCase()
  );
  const missingHeaders = REQUIRED_HEADERS.filter(
    (requiredHeader) => !headers.includes(requiredHeader)
  );
  if (missingHeaders.length > 0) {
    throw new Error(
      `Missing required CSV column${missingHeaders.length === 1 ? '' : 's'}: ${missingHeaders.join(', ')}.`
    );
  }

  const duplicateHeaders = REQUIRED_HEADERS.filter(
    (requiredHeader) =>
      headers.filter((header) => header === requiredHeader).length > 1
  );
  if (duplicateHeaders.length > 0) {
    throw new Error(
      `Duplicate CSV column${duplicateHeaders.length === 1 ? '' : 's'}: ${duplicateHeaders.join(', ')}.`
    );
  }

  const columnIndexes = {
    name: headers.indexOf('name'),
    email: headers.indexOf('email'),
    password: headers.indexOf('password'),
  };
  const recordRows = rows.slice(1);
  const rowErrors: string[] = [];
  const users: BulkUserPayload[] = [];

  recordRows.forEach((row, index) => {
    const recordNumber = index + 1;
    const name = cellToString(row[columnIndexes.name]).trim();
    const email = cellToString(row[columnIndexes.email]).trim();
    const password = cellToString(row[columnIndexes.password]);

    if (!name && !email && !password) {
      return;
    }

    const missingValues = REQUIRED_HEADERS.filter((header) => {
      const value = { name, email, password }[header];
      return value.length === 0;
    });

    if (missingValues.length > 0) {
      rowErrors.push(
        `Record ${recordNumber} is missing ${missingValues.join(', ')}.`
      );
      return;
    }

    users.push({
      name,
      email,
      password,
      active: true,
      client_name: clientName,
      role: Role.ClientUser,
    });
  });

  if (rowErrors.length > 0) {
    const visibleErrors = rowErrors.slice(0, 5).join(' ');
    const remainingCount = rowErrors.length - 5;
    throw new Error(
      remainingCount > 0
        ? `${visibleErrors} ${remainingCount} additional row errors were found.`
        : visibleErrors
    );
  }

  if (users.length > MAX_BULK_USERS) {
    throw new Error(
      `The CSV file contains ${users.length.toLocaleString('en-US')} users. Bulk user uploads are limited to ${MAX_BULK_USERS.toLocaleString('en-US')} users.`
    );
  }

  if (users.length === 0) {
    throw new Error('The CSV file does not contain any user records.');
  }

  return users;
};
